import React, { useEffect, useMemo, useState } from 'react';

import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Switch } from 'components/ui/switch';
import { Textarea } from 'components/ui/textarea';
import { useToast } from 'components/ui/use-toast';
import {
  aiSettings,
  type AIFeatureRevision,
  type AIFeatureSetting,
  type AIFeatureUpdate,
  type AIModelCatalog,
  type AIPromptMessage,
  type AISettingsField,
  type AISettingsValue,
  type SettingsClient,
} from 'services/api/aiSettingsClient';

type DraftValue = string | boolean | AIPromptMessage[];
type Draft = Record<string, DraftValue>;

const EMPTY_CATALOG: AIModelCatalog = {
  models: [], fetched_at: null, last_error: '', last_error_at: null, api_key_configured: false,
};

const PLACEHOLDER_PATTERN = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;

const toDraftValue = (value: AISettingsValue | undefined): DraftValue => {
  if (Array.isArray(value)) return value.map((message) => ({ ...message }));
  if (typeof value === 'boolean') return value;
  return value === undefined || value === null ? '' : String(value);
};

// A stale saved prompt starts in the draft so staff can review it and save it again.
const buildDraft = (feature: AIFeatureSetting): Draft =>
  Object.fromEntries(feature.fields.map((field) => [
    field.key,
    toDraftValue(feature.stale_overrides?.[field.key] ?? feature.effective[field.key]),
  ]));

const placeholdersIn = (messages: AIPromptMessage[]): Set<string> => {
  const found = new Set<string>();
  messages.forEach((message) => {
    Array.from(message.content.matchAll(PLACEHOLDER_PATTERN)).forEach((match) => found.add(match[1]));
  });
  return found;
};

const sameValue = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right);

const fromDraft = (field: AISettingsField, value: DraftValue): AISettingsValue => {
  if (field.kind === 'int' || field.kind === 'float') {
    const text = String(value).trim();
    const number = Number(text);
    return text === '' || Number.isNaN(number) ? text : number;
  }
  return value;
};

const formatTimestamp = (value: string | null) => (value ? new Date(value).toLocaleString() : '');

const describeValue = (value: AISettingsValue | undefined): string => {
  if (value === undefined) return 'default';
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  if (Array.isArray(value)) return 'edited prompt';
  if (typeof value === 'string' && value.length > 60) return 'edited text';
  return String(value) || '(blank)';
};

const describeRevision = (revision: AIFeatureRevision): string => {
  if (revision.action === 'reset') return 'Reset to defaults';
  const changed = Object.entries(revision.values).map(([key, value]) => `${key}: ${describeValue(value)}`);
  const cleared = Object.keys(revision.previous_values).filter((key) => !(key in revision.values));
  const parts = [...changed, ...cleared.map((key) => `${key}: default`)];
  return parts.length ? parts.join(', ') : 'No overrides';
};

type ModelSelectProps = {
  id: string;
  field: AISettingsField;
  value: string;
  defaultValue: string;
  catalog: AIModelCatalog;
  disabled: boolean;
  onChange: (value: string) => void;
};

const ModelSelect: React.FC<ModelSelectProps> = ({ id, field, value, defaultValue, catalog, disabled, onChange }) => {
  const available = catalog.models
    .filter((model) => !field.model_kind || model.kind === field.model_kind)
    .map((model) => model.id);
  const availableSet = new Set(available);
  const extras = [value, defaultValue].filter((modelId, index, list) => modelId && !availableSet.has(modelId) && list.indexOf(modelId) === index);

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id} aria-label={field.label}>
        <SelectValue placeholder="Choose a model" />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        {extras.length ? (
          <SelectGroup>
            {extras.map((modelId) => (
              <SelectItem key={modelId} value={modelId}>
                {modelId}{catalog.models.length ? ' (not in model list)' : ''}
              </SelectItem>
            ))}
          </SelectGroup>
        ) : null}
        {available.map((modelId) => (
          <SelectItem key={modelId} value={modelId}>
            {modelId}{modelId === defaultValue ? ' (default)' : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

type AIFeatureEditorProps = {
  feature: AIFeatureSetting;
  /** Model list for model fields; app settings have none. */
  catalog?: AIModelCatalog;
  /** Endpoints for this setting's area; AI Settings by default. */
  client?: SettingsClient;
  onSaved: (feature: AIFeatureSetting) => void;
  onClose: () => void;
};

const AIFeatureEditor: React.FC<AIFeatureEditorProps> = ({
  feature, catalog = EMPTY_CATALOG, client = aiSettings, onSaved, onClose,
}) => {
  const { addToast } = useToast();
  // The saved settings this draft was built from. Saves send its revision, so a
  // draft opened before another save finished gets a conflict instead of
  // silently reverting that save.
  const [base, setBase] = useState<AIFeatureSetting>(feature);
  const [draft, setDraft] = useState<Draft>(() => buildDraft(feature));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [history, setHistory] = useState<AIFeatureRevision[] | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const changes = useMemo(() => {
    const update: AIFeatureUpdate = {};
    base.fields.forEach((field) => {
      const next = fromDraft(field, draft[field.key]);
      if (!sameValue(next, base.effective[field.key])) {
        update[field.key] = next;
      }
    });
    return update;
  }, [draft, base]);
  const hasChanges = Object.keys(changes).length > 0;
  const hasOverrides = Object.keys(base.overrides).length > 0 || Boolean(base.stale_fields?.length);

  // Adopt newer saved settings when there are no local edits to protect.
  useEffect(() => {
    if (feature.revision !== base.revision && !hasChanges) {
      setBase(feature);
      setDraft(buildDraft(feature));
    }
  }, [feature, base.revision, hasChanges]);

  const applyServerFeature = (next: AIFeatureSetting) => {
    onSaved(next);
    setBase(next);
    setDraft(buildDraft(next));
    setErrors({});
    setHistory(null);
  };

  const handleError = (error: unknown, fallbackTitle: string) => {
    const response = (error as { response?: { status?: number; data?: Record<string, unknown> } })?.response;
    if (response?.status === 409 && response.data?.feature) {
      // Take the other save's values for fields you didn't touch and keep your edits.
      const latest = response.data.feature as AIFeatureSetting;
      onSaved(latest);
      setDraft((current) => Object.fromEntries(latest.fields.map((field) => {
        const untouched = sameValue(fromDraft(field, current[field.key]), base.effective[field.key]);
        return [field.key, untouched ? toDraftValue(latest.effective[field.key]) : current[field.key]];
      })));
      setBase(latest);
      setHistory(null);
      addToast({
        title: 'Settings changed elsewhere',
        description: 'Loaded the other changes and kept your edits. Review them and save again.',
        variant: 'destructive',
      });
      return;
    }
    const fieldErrors = response?.data?.errors;
    if (fieldErrors && typeof fieldErrors === 'object') {
      setErrors(fieldErrors as Record<string, string>);
      addToast({ title: 'Check the highlighted settings', variant: 'destructive' });
      return;
    }
    addToast({ title: fallbackTitle, description: 'Please try again later.', variant: 'destructive' });
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const response = await client.updateFeature(feature.key, changes, base.revision);
      applyServerFeature(response.data);
      addToast({ title: `${feature.label} updated`, description: 'New requests use these settings.' });
    } catch (error) {
      handleError(error, 'Failed to save AI settings');
    } finally {
      setIsSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm(`Reset ${feature.label} to its shipped defaults?`)) return;
    setIsSaving(true);
    try {
      const response = await client.resetFeature(feature.key, base.revision);
      applyServerFeature(response.data);
      addToast({ title: `${feature.label} reset`, description: 'New requests use the shipped defaults.' });
    } catch (error) {
      handleError(error, 'Failed to reset AI settings');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleHistory = async () => {
    if (history) {
      setHistory(null);
      return;
    }
    setIsLoadingHistory(true);
    try {
      const response = await client.getHistory(feature.key);
      setHistory(response.data.revisions);
    } catch {
      addToast({ title: 'Failed to load change history', variant: 'destructive' });
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const restore = async (revision: number) => {
    setIsSaving(true);
    try {
      const response = await client.restoreRevision(feature.key, revision, base.revision);
      applyServerFeature(response.data);
      addToast({ title: `Restored revision ${revision}` });
    } catch (error) {
      handleError(error, 'Failed to restore revision');
    } finally {
      setIsSaving(false);
    }
  };

  const setField = (key: string, value: DraftValue) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const renderField = (field: AISettingsField) => {
    const id = `ai-${feature.key}-${field.key}`;
    const value = draft[field.key];
    const defaultValue = base.defaults[field.key];
    const defaultLabel = typeof defaultValue === 'boolean'
      ? (defaultValue ? 'On' : 'Off')
      : field.choice_labels?.[String(defaultValue)] ?? String(defaultValue);
    const isOverridden = field.key in base.overrides;
    const defaultHint = isOverridden && !Array.isArray(defaultValue) && defaultLabel.length <= 60
      ? `Default: ${defaultLabel || '(blank)'}`
      : null;

    let control: React.ReactNode;
    if (field.kind === 'model') {
      control = (
        <ModelSelect
          id={id}
          field={field}
          value={String(value)}
          defaultValue={String(defaultValue ?? '')}
          catalog={catalog}
          disabled={isSaving}
          onChange={(next) => setField(field.key, next)}
        />
      );
    } else if (field.kind === 'choice') {
      control = (
        <Select value={String(value)} onValueChange={(next) => setField(field.key, next)} disabled={isSaving}>
          <SelectTrigger id={id} aria-label={field.label}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(field.choices ?? []).map((choice) => (
              <SelectItem key={choice} value={choice}>
                {field.choice_labels?.[choice] ?? choice}{choice === defaultValue ? ' (default)' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    } else if (field.kind === 'text') {
      control = (
        <Textarea
          id={id}
          value={String(value)}
          rows={12}
          className="font-mono text-xs leading-relaxed"
          disabled={isSaving}
          onChange={(event) => setField(field.key, event.target.value)}
        />
      );
    } else if (field.kind === 'messages') {
      const messages = value as AIPromptMessage[];
      const used = placeholdersIn(messages);
      const missing = feature.prompt_variables.filter((name) => !used.has(name));
      control = (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5" aria-label="Prompt placeholders">
            {feature.prompt_variables.map((name) => (
              <code
                key={name}
                className={`rounded-md px-1.5 py-0.5 text-xs ${used.has(name) ? 'bg-muted text-foreground' : 'bg-destructive/10 text-destructive'}`}
              >
                {`{{${name}}}`}
              </code>
            ))}
          </div>
          {messages.map((message, index) => (
            <div key={`${message.role}-${index}`} className="space-y-1">
              <Label htmlFor={`${id}-${index}`} className="text-xs uppercase tracking-wide text-muted-foreground">
                {`Message ${index + 1} · ${message.role}`}
              </Label>
              <Textarea
                id={`${id}-${index}`}
                value={message.content}
                rows={Math.min(16, Math.max(3, message.content.split('\n').length + 1))}
                className="font-mono text-xs leading-relaxed"
                disabled={isSaving}
                onChange={(event) => setField(field.key, messages.map((item, itemIndex) => (
                  itemIndex === index ? { ...item, content: event.target.value } : item
                )))}
              />
            </div>
          ))}
          {base.stale_fields?.includes('messages') ? (
            <p className="text-xs text-destructive" role="alert">
              This saved prompt was written for an older shipped version, so the shipped prompt is in use. Review it below and save to use it again, or reset.
            </p>
          ) : null}
          {missing.length ? (
            <p className="text-xs text-destructive" role="alert">
              {`Missing ${missing.map((name) => `{{${name}}}`).join(', ')}`}
            </p>
          ) : null}
        </div>
      );
    } else if (field.kind === 'boolean') {
      control = (
        <div className="flex items-center gap-3">
          <Switch
            id={id}
            aria-label={field.label}
            checked={value === true}
            disabled={isSaving}
            onCheckedChange={(checked) => setField(field.key, checked)}
          />
          <span className="text-sm text-foreground">{value === true ? 'On' : 'Off'}</span>
        </div>
      );
    } else {
      control = (
        <Input
          id={id}
          type={field.kind === 'email' ? 'email' : 'text'}
          value={String(value)}
          inputMode={field.kind === 'int' || field.kind === 'float' ? 'decimal' : undefined}
          disabled={isSaving}
          onChange={(event) => setField(field.key, event.target.value)}
        />
      );
    }

    return (
      <div key={field.key} className={field.kind === 'messages' || field.kind === 'text' ? 'space-y-1.5 md:col-span-2' : 'space-y-1.5'}>
        <Label htmlFor={field.kind === 'messages' ? `${id}-0` : id} className="text-sm font-medium text-foreground">
          {field.label}
        </Label>
        {control}
        {errors[field.key] ? (
          <p className="text-xs text-destructive" role="alert">{errors[field.key]}</p>
        ) : field.help || defaultHint ? (
          <p className="text-xs text-muted-foreground">{[defaultHint, field.help].filter(Boolean).join(' · ')}</p>
        ) : null}
      </div>
    );
  };

  return (
    <div className="space-y-5 border-t border-border pt-5" data-testid={`ai-feature-editor-${feature.key}`}>
      <div className="grid gap-4 md:grid-cols-2">{feature.fields.map(renderField)}</div>

      {base.fields.some((field) => field.key === 'service_tier') ? (
        <p className="text-xs text-muted-foreground" data-testid="ai-provider-check">
          {base.provider_check ? (
            <>
              {`Last saved configuration check: ${formatTimestamp(base.provider_check.checked_at)}. `}
              {`Requested tier: ${base.provider_check.requested_service_tier ?? 'not specified'}; returned tier: ${base.provider_check.service_tier ?? 'not reported'}. `}
              OpenAI accepted a small test request. This observation does not guarantee the tier or speed of future requests.
            </>
          ) : 'This configuration has no saved provider check. Changing the model or processing settings runs a small compatibility check when saved.'}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="auto" onClick={() => { void save(); }} disabled={!hasChanges || isSaving}>
          {isSaving ? 'Saving...' : 'Save changes'}
        </Button>
        <Button type="button" size="auto" variant="secondary" onClick={onClose} disabled={isSaving}>
          Close
        </Button>
        {hasOverrides ? (
          <Button type="button" size="auto" variant="outline" onClick={() => { void reset(); }} disabled={isSaving}>
            Reset to defaults
          </Button>
        ) : null}
        <Button
          type="button"
          size="auto"
          variant="ghost"
          aria-expanded={Boolean(history)}
          onClick={() => { void toggleHistory(); }}
          disabled={isLoadingHistory}
        >
          {history ? 'Hide history' : 'History'}
        </Button>
      </div>

      {history ? (
        history.length ? (
          <ol className="space-y-2" aria-label={`${feature.label} change history`}>
            {history.map((item) => (
              <li key={item.revision} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border px-4 py-3 text-sm">
                <div className="min-w-0 space-y-0.5">
                  <p className="font-medium text-foreground">{`r${item.revision} · ${describeRevision(item)}`}</p>
                  <p className="text-xs text-muted-foreground">
                    {[formatTimestamp(item.created_at), item.created_by, item.note].filter(Boolean).join(' · ')}
                  </p>
                </div>
                {item.revision !== base.revision && item.action !== 'reset' ? (
                  <Button type="button" size="auto" variant="secondary" onClick={() => { void restore(item.revision); }} disabled={isSaving}>
                    Restore
                  </Button>
                ) : null}
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground">No changes yet.</p>
        )
      ) : null}
    </div>
  );
};

export default AIFeatureEditor;
